// src/components/WhtReport.js
// Withholding Tax report: WHT withheld from vendors, per supplier — the figures
// for supplier WHT certificates and the WHT return.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, Alert, CircularProgress, IconButton
} from '@mui/material';
import { Home, Print, ReceiptLong, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const todayStr    = () => new Date().toISOString().split('T')[0];

const WhtReport = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [from, setFrom]       = useState(firstOfYear());
  const [to, setTo]           = useState(todayStr());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const run = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/payables/reports/wht`, { headers: hdrs(), params: { from, to } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load WHT report'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { run(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      [company?.name || 'WITHHOLDING TAX'],
      ['WITHHOLDING TAX REPORT'],
      [`Period: ${from} to ${to}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Supplier', 'PIN', 'Payments', 'Gross Base', 'WHT'],
      ...data.rows.map(r => [r.supplierName, r.pin, r.count, r.base, r.whtAmount]),
      [],
      ['', '', '', data.totals.base, data.totals.whtAmount],
    ];
    downloadCsv(rows, `wht_${from}_to_${to}.csv`);
  };

  const t = data?.totals;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .wht-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField type="date" label="From" value={from} size="small"
          onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="To" value={to} size="small"
          onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#e6510015' }}>
          {loading ? <CircularProgress size={20} /> : <ReceiptLong />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="wht-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="WITHHOLDING TAX" subtitle={`${from} → ${to}`} />
          {data && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PIN</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Payments</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Gross Base</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>WHT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">No withholding tax recorded for this period</TableCell></TableRow>
                  ) : data.rows.map(r => (
                    <TableRow key={r.supplierCode} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                      <TableCell>{r.supplierName}</TableCell>
                      <TableCell>{r.pin || '—'}</TableCell>
                      <TableCell align="right">{r.count}</TableCell>
                      <TableCell align="right">{fmt(r.base)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(r.whtAmount)}</TableCell>
                    </TableRow>
                  ))}
                  {data.rows.length > 0 && (
                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>TOTALS</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(t.base)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.05rem' }}>{sym()} {fmt(t.whtAmount)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhtReport;
